import { cn } from '@compass/utils'
import { InferFullSearchSchema } from '@tanstack/react-router'
import { LinkButtonLink } from 'components/compass-integration'
import { InlineCopyButton } from 'components/InlineCopyButton'
import { ListFilterPlusIcon } from 'lucide-react'
import { ampli } from 'models/ampli'
import { Children, DetailedHTMLProps, HTMLAttributes } from 'react'

import { useEventsTableActions } from './EventsTableActions'
import { RoutesWithEventSearchSchema } from './RoutesWithEventSearchSchema'

type EventsFilterSearch = {
  bot?: 'all' | 'good' | 'bad' | 'none'
  [key: string]: unknown
}

export function CellAction({
  children,
  copyValue,
  search,
  wrapperClassName,
  className,
  contentClassName,
  align = 'start',
  field,
  fields,
  extraActions,
  disableMask = false,
}: {
  children?: React.ReactNode
  copyValue?: string
  search?: (old: InferFullSearchSchema<RoutesWithEventSearchSchema>) => EventsFilterSearch
  wrapperClassName?: string
  className?: string
  /** Classes applied to the content wrapper only when a hover action renders (e.g. padding to shift right-aligned content aside). */
  contentClassName?: string
  align?: 'start' | 'center' | 'end'
  field?: string
  fields?: string[]
  /**
   * Extra hover-reveal action buttons (e.g. surface-specific IP include/exclude).
   * Pass an array so each button is counted toward the hover mask width — a
   * single fragment would otherwise count as one action.
   */
  extraActions?: React.ReactNode[]
  /** Disable the gradient mask on hover (useful when cell content has its own hover states) */
  disableMask?: boolean
}) {
  // The surface (page) decides whether a "filter by value" action is available.
  // identificationVisits provides a `from` route; visitorDetails provides none, so
  // the filter link degrades gracefully and cells rely on the surface's own filters.
  const { from } = useEventsTableActions()

  const canFilter = Boolean(from && search)
  // Count every visible button so the mask is wide enough to sit under all of
  // them. `React.Children.count` counts each element of the extraActions array.
  const actionCount = (copyValue ? 1 : 0) + (canFilter ? 1 : 0) + Children.count(extraActions)
  const transparentWidth = `${actionCount * 1.75 + (actionCount - 1) * 0.25}rem`
  const shouldMask = actionCount > 0 && !disableMask

  return (
    <CellWrapper
      className={cn('relative group/target', wrapperClassName)}
      style={
        {
          '--transparent-width': transparentWidth,
        } as any
      }
    >
      <div
        className={cn(
          'w-full h-full',
          shouldMask &&
            align === 'start' &&
            'group-hover/target:mask-[linear-gradient(to_right,black_calc(100%-var(--transparent-width)-1rem),transparent_calc(100%-var(--transparent-width)))] group-hover/cell:mask-[linear-gradient(to_right,black_calc(100%-var(--transparent-width)-1rem),transparent_calc(100%-var(--transparent-width)))]',
          shouldMask &&
            align === 'end' &&
            'group-hover/target:mask-[linear-gradient(to_right,transparent_var(--transparent-width),black_calc(var(--transparent-width)+1rem))] group-hover/cell:mask-[linear-gradient(to_right,transparent_var(--transparent-width),black_calc(var(--transparent-width)+1rem))]',
          // Surfaces can opt cell content into shifting aside for the hover actions
          // (e.g. right-aligned numbers). Only applies when an action actually renders,
          // so cells without actions do not jump on hover.
          actionCount > 0 && contentClassName,
          className
        )}
      >
        {children}
      </div>
      <div
        className={cn([
          'pointer-events-none',
          'absolute top-0 bottom-0',
          'z-10',
          'opacity-0',
          'group-hover/target:opacity-100 group-hover/target:pointer-events-auto',
          'group-hover/cell:opacity-100 group-hover/cell:pointer-events-auto',
          'focus-within:opacity-100 focus-within:pointer-events-auto',
          'transition',
          'flex items-center gap-2',
          (align === 'start' || align === 'center') && 'right-2',
          align === 'end' && 'left-2',
        ])}
      >
        {copyValue && (
          <InlineCopyButton
            copyValue={copyValue}
            onCopy={() => field && ampli.identificationEventTableValueCopied({ fieldCopied: field })}
          />
        )}
        {extraActions}
        {from && search && (
          <LinkButtonLink
            from={from}
            to='.'
            search={search}
            variant='secondary'
            size='sm'
            isIcon
            aria-label='Filter by value'
            onPress={() => {
              if (fields || field) {
                ampli.attributeFilterApplied({
                  datasetFocus: 'Identification Events',
                  attributeFilters: (fields ?? [field]) as string[],
                  filterType: 'filterByValue',
                })
              }

              // Make sure that after clicking the button, the focus shifts to the table for keyboard users.
              const element = document.activeElement
              if (element instanceof HTMLElement) {
                element.blur()
              }
            }}
          >
            <ListFilterPlusIcon size={16} />
          </LinkButtonLink>
        )}
      </div>
    </CellWrapper>
  )
}

export function CellWrapper({
  children,
  className,
  ...props
}: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>) {
  return (
    <div className={cn('px-(--spacing-x) py-(--spacing-y)', className)} {...props}>
      {children}
    </div>
  )
}
